import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaFileInvoiceDollar,
  FaSpinner,
  FaDownload,
  FaEnvelope,
  FaExclamationCircle,
} from 'react-icons/fa';
import { orderService } from '../../../../services/apiService';
import invoiceService from '../../../../services/invoiceService';
import { formatPrice } from '../../../../utils/currency';

const Invoices = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [invoicesByOrder, setInvoicesByOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await orderService.getOrders();
        const list = res?.orders ?? res?.data?.orders ?? [];
        setOrders(list);
        const map = {};
        for (const order of list) {
          const id = order.orderId || order._id;
          if (!id) continue;
          try {
            const inv = await invoiceService.getInvoiceByOrder(id);
            if (inv?.invoiceNumber) map[id] = inv;
          } catch (_) {
            // no invoice for this order
          }
        }
        setInvoicesByOrder(map);
      } catch (e) {
        setOrders([]);
        setInvoicesByOrder({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownload = async (invoiceNumber) => {
    if (!invoiceNumber) return;
    try {
      setDownloading(invoiceNumber);
      const blob = await invoiceService.downloadInvoicePDF(invoiceNumber);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(null);
    }
  };

  const handleSendEmail = async (invoiceNumber) => {
    if (!invoiceNumber) return;
    try {
      await invoiceService.sendInvoiceEmail(invoiceNumber);
      alert('Invoice sent to your email.');
    } catch (e) {
      alert(e.message || 'Failed to send email.');
    }
  };

  const ordersWithInvoices = orders.filter(
    (o) => invoicesByOrder[o.orderId || o._id]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <FaSpinner className="animate-spin text-2xl text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Invoices</h2>
        <p className="text-gray-600 mt-1">View and download invoices for your orders.</p>
      </div>

      {ordersWithInvoices.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <FaFileInvoiceDollar className="mx-auto text-4xl text-gray-300 mb-4" />
          <p className="text-gray-600 mb-2">No invoices yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Invoices are generated for paid or delivered orders.
          </p>
          <button
            onClick={() => navigate('/customer/dashboard/orders')}
            className="text-red-600 font-medium text-sm hover:underline"
          >
            View orders
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {ordersWithInvoices.map((order) => {
            const id = order.orderId || order._id;
            const inv = invoicesByOrder[id];
            if (!inv) return null;
            const isDownloading = downloading === inv.invoiceNumber;
            return (
              <div
                key={id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <FaFileInvoiceDollar className="text-xl text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      Invoice #{inv.invoiceNumber || inv.invoice_number || '—'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Order #{String(order.orderId || order._id || '').slice(-8)} ·{' '}
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString()
                        : '—'}
                    </p>
                    {(inv.total != null || inv.amount != null) && (
                      <p className="text-sm font-medium text-gray-800 mt-1">
                        {formatPrice(Number(inv.total ?? inv.amount ?? 0))}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(inv.invoiceNumber || inv.invoice_number)}
                    disabled={isDownloading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <FaDownload />
                    )}
                    Download
                  </button>
                  <button
                    onClick={() => handleSendEmail(inv.invoiceNumber || inv.invoice_number)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium"
                  >
                    <FaEnvelope />
                    Email
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {orders.length > 0 && ordersWithInvoices.length === 0 && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <FaExclamationCircle />
          <span>You have orders but no invoices yet. Invoices are created for paid/delivered orders.</span>
        </div>
      )}
    </div>
  );
};

export default Invoices;
